/**
 * Projects API service.
 * Handles all project-related API calls with role-based access.
 */
import api from './api';

export interface Project {
  id: number;
  name: string;
  description?: string;
  type: 'kubernetes' | 'aws' | 'gcp' | 'azure' | 'data';
  status: 'pending' | 'provisioning' | 'ready' | 'failed' | 'archived';
  cloud_provider?: string;
  region?: string;
  environment?: 'development' | 'staging' | 'production';
  auto_scaling: boolean;
  monitoring: boolean;
  owner_auth0_sub: string;
  created_at: string;
  updated_at?: string;
  assigned_users?: Array<{
    auth0_sub: string;
    email: string;
    name?: string;
  }>;
}

export interface ProjectCreate {
  name: string;
  description?: string;
  type: string;
  cloud_provider?: string;
  region?: string;
  environment?: string;
  auto_scaling?: boolean;
  monitoring?: boolean;
}

export interface ProjectUpdate {
  name?: string;
  description?: string;
  type?: string;
  status?: string;
  cloud_provider?: string;
  region?: string;
  environment?: string;
  auto_scaling?: boolean;
  monitoring?: boolean;
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Fetch list of projects with optional filtering and pagination.
 * Admins see all projects, developers see only assigned projects.
 */
export const getProjects = async (params?: {
  page?: number;
  page_size?: number;
  status_filter?: string;
}): Promise<ProjectListResponse> => {
  const response = await api.get('/api/v1/projects', { params });
  return response.data;
};

/**
 * Get a specific project by ID.
 */
export const getProject = async (projectId: number): Promise<Project> => {
  const response = await api.get(`/api/v1/projects/${projectId}`);
  return response.data;
};

/**
 * Create a new project (admin only).
 */
export const createProject = async (projectData: ProjectCreate): Promise<Project> => {
  const response = await api.post('/api/v1/projects', projectData);
  return response.data;
};

/**
 * Update an existing project (admin only).
 */
export const updateProject = async (
  projectId: number,
  projectData: ProjectUpdate
): Promise<Project> => {
  const response = await api.patch(`/api/v1/projects/${projectId}`, projectData);
  return response.data;
};

/**
 * Delete a project (admin only).
 */
export const deleteProject = async (projectId: number): Promise<void> => {
  await api.delete(`/api/v1/projects/${projectId}`);
};

/**
 * Assign users to a project (admin only).
 */
export const assignUsers = async (
  projectId: number,
  userAuth0Subs: string[]
): Promise<Project> => {
  const response = await api.post(`/api/v1/projects/${projectId}/assignments`, {
    user_auth0_subs: userAuth0Subs,
  });
  return response.data;
};

/**
 * Remove users from a project (admin only).
 */
export const removeUsers = async (
  projectId: number,
  userAuth0Subs: string[]
): Promise<Project> => {
  const response = await api.delete(`/api/v1/projects/${projectId}/assignments`, {
    data: { user_auth0_subs: userAuth0Subs },
  });
  return response.data;
};
